import React, { useCallback, useContext, useState } from "react";
import {
  Avatar,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  TextField,
  Tooltip,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import VisibilityIcon from "@mui/icons-material/Visibility";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import ArchiveIcon from "@mui/icons-material/Archive";
import UnarchiveIcon from "@mui/icons-material/Unarchive";
import { Link as RouterLink, useHistory } from "react-router-dom";
import { useIntl } from "react-intl";
import { useSnackbar } from "notistack";
import UserContext from "context/UserContext";
import EmptyPage from "components/common/EmptyPage";
import CsvDownloadButton, { csvFilename } from "components/common/CsvDownloadButton";
import performRequest from "network/perform-request";
import {
  leagueCustomerTeamImagesRoute,
  leagueCustomerTeamRoute,
  leagueCustomerTeamsCsvRoute,
  leagueCustomerTeamsRoute,
} from "network/api-routes";
import { GESTION_ROUTES } from "constants/gestion-routes";
import { useGestionCustomer } from "hooks/useGestionCustomer";
import { useGestionPaginatedList } from "hooks/useGestionPaginatedList";
import GestionListPagination from "components/pages/Gestion/GestionListPagination";
import TeamImageUpload from "components/pages/Gestion/TeamImageUpload";
import { teamImageUrl } from "components/pages/Gestion/team-image-helpers";
import GestionTable from "components/pages/Gestion/GestionTable";
import GestionPageHeader from "components/pages/Gestion/GestionPageHeader";
import FinanceOnly from "components/pages/Gestion/FinanceOnly";

const emptyTeamForm = { name: "", short_name: "" };

const GestionTeams = () => {
  const { customerId, customerName } = useGestionCustomer();
  const { user } = useContext(UserContext);
  const intl = useIntl();
  const { enqueueSnackbar } = useSnackbar();
  const history = useHistory();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingTeam, setEditingTeam] = useState(null);
  const [form, setForm] = useState(emptyTeamForm);
  const [saving, setSaving] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [pendingImageFile, setPendingImageFile] = useState(null);

  const handleFetchError = useCallback(
    (error) => {
      enqueueSnackbar(error?.response?.data?.message || intl.formatMessage({ id: "gestion.error.generic" }), {
        variant: "error",
      });
    },
    [enqueueSnackbar, intl],
  );

  const {
    items: teams,
    search,
    page,
    pageSize,
    totalPages,
    totalResults,
    initialLoading,
    refreshing,
    handleSearchChange,
    handlePageChange,
    handlePageSizeChange,
    reload,
  } = useGestionPaginatedList({
    customerId,
    userId: user?.id,
    user,
    route: leagueCustomerTeamsRoute,
    onError: handleFetchError,
  });

  const openCreateDialog = () => {
    setEditingTeam(null);
    setForm(emptyTeamForm);
    setPendingImageFile(null);
    setDialogOpen(true);
  };

  const openEditDialog = (team) => {
    setEditingTeam(team);
    setForm({ name: team.name || "", short_name: team.short_name || "" });
    setPendingImageFile(null);
    setDialogOpen(true);
  };

  const uploadTeamImage = (teamId, file) => {
    if (!customerId || !teamId || !file) return Promise.resolve(null);
    const body = new FormData();
    body.append("image", file);
    setUploadingImage(true);
    return performRequest("POST", leagueCustomerTeamImagesRoute(customerId, teamId), body, true, user)
      .then((response) => response.data)
      .finally(() => setUploadingImage(false));
  };

  const handleImageSelect = (file) => {
    if (!file) return;
    if (editingTeam?.id) {
      uploadTeamImage(editingTeam.id, file)
        .then((updatedTeam) => {
          setEditingTeam((prev) => ({ ...prev, ...updatedTeam }));
          enqueueSnackbar(intl.formatMessage({ id: "gestion.teams.crestUploaded" }), { variant: "success" });
          reload();
        })
        .catch(handleFetchError);
      return;
    }
    setPendingImageFile(file);
  };

  const handleSave = () => {
    if (!customerId || !form.name.trim()) return;
    setSaving(true);
    const method = editingTeam ? "PATCH" : "POST";
    const url = editingTeam
      ? leagueCustomerTeamRoute(customerId, editingTeam.id)
      : leagueCustomerTeamsRoute(customerId);

    performRequest(method, url, form, true, user)
      .then((response) => {
        const savedTeam = response.data;
        const uploads = [];
        if (pendingImageFile && savedTeam?.id) {
          uploads.push(
            uploadTeamImage(savedTeam.id, pendingImageFile).then(() => {
              enqueueSnackbar(intl.formatMessage({ id: "gestion.teams.crestUploaded" }), { variant: "success" });
            }),
          );
        }
        return Promise.all(uploads).then(() => savedTeam);
      })
      .then(() => {
        enqueueSnackbar(
          intl.formatMessage({ id: editingTeam ? "gestion.teams.updated" : "gestion.teams.created" }),
          { variant: "success" },
        );
        setDialogOpen(false);
        setPendingImageFile(null);
        reload();
      })
      .catch(handleFetchError)
      .finally(() => setSaving(false));
  };

  const toggleArchive = (team) => {
    if (!customerId) return;
    performRequest("PATCH", leagueCustomerTeamRoute(customerId, team.id), { archived: !team.archived }, true, user)
      .then(() => reload())
      .catch(handleFetchError);
  };

  if (!customerId) return null;

  return (
    <Box sx={{ width: "100%", maxWidth: "lg", mx: "auto", p: { xs: 3, md: 4 } }}>
      <Stack spacing={3}>
        <GestionPageHeader
          title={intl.formatMessage({ id: "gestion.teams.title" }, { name: customerName })}
          subtitle={intl.formatMessage({ id: "gestion.teams.subtitle" })}
          actions={
            <>
              <Button variant="contained" color="secondary" size="small" startIcon={<AddIcon />} onClick={openCreateDialog}>
                {intl.formatMessage({ id: "gestion.teams.create" })}
              </Button>
              <CsvDownloadButton
                labelId="league.csv.exportTeams"
                url={leagueCustomerTeamsCsvRoute(customerId)}
                filename={csvFilename(customerName, "teams")}
                user={user}
              />
            </>
          }
        />

        <TextField
          size="small"
          fullWidth
          label={intl.formatMessage({ id: "Por nombre" })}
          value={search}
          onChange={handleSearchChange}
        />

      <GestionListPagination
        page={page}
        totalPages={totalPages}
        pageSize={pageSize}
        totalResults={totalResults}
        onPageChange={handlePageChange}
        onPageSizeChange={handlePageSizeChange}
        disabled={initialLoading || refreshing}
      />

      {initialLoading ? (
        <Box display="flex" justifyContent="center" sx={{ py: 6 }}>
          <CircularProgress />
        </Box>
      ) : teams.length === 0 ? (
        <EmptyPage text={intl.formatMessage({ id: "gestion.teams.empty" })} />
      ) : (
        <Box sx={{ position: "relative", opacity: refreshing ? 0.6 : 1, transition: "opacity 0.2s" }}>
          {refreshing && (
            <CircularProgress size={24} sx={{ position: "absolute", top: 16, right: 16, zIndex: 1 }} />
          )}
          <GestionTable
            getRowSx={(team) => ({ opacity: team.archived ? 0.6 : 1 })}
            getRowLabel={(team) => `${intl.formatMessage({ id: "gestion.teamDetail.view" })}: ${team.name}`}
            onRowClick={(team) => history.push(GESTION_ROUTES.team(customerId, team.id))}
            headers={[
              {
                key: "avatar",
                title: "",
                width: 56,
                renderCell: (_value, team) => (
                  <Avatar
                    src={teamImageUrl(team, "small") || undefined}
                    variant="rounded"
                    sx={{ width: 36, height: 36, bgcolor: "action.hover" }}
                  >
                    {team.short_name?.slice(0, 2)?.toUpperCase() || "?"}
                  </Avatar>
                ),
              },
              {
                key: "name",
                title: intl.formatMessage({ id: "gestion.teams.name" }),
                renderCell: (_value, team) => (
                  <RouterLink to={GESTION_ROUTES.team(customerId, team.id)} style={{ textDecoration: "none", color: "inherit" }}>
                    {team.name}
                  </RouterLink>
                ),
              },
              { key: "short_name", title: intl.formatMessage({ id: "gestion.teams.shortName" }) },
              {
                key: "actions",
                title: intl.formatMessage({ id: "gestion.actions" }),
                align: "right",
                stopRowClick: true,
                renderCell: (_value, team) => (
                  <>
                    <Tooltip title={intl.formatMessage({ id: "gestion.teamDetail.view" })}>
                      <IconButton component={RouterLink} to={GESTION_ROUTES.team(customerId, team.id)} size="small">
                        <VisibilityIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <FinanceOnly>
                      <Tooltip title={intl.formatMessage({ id: "gestion.players.financials" })}>
                        <IconButton
                          component={RouterLink}
                          to={GESTION_ROUTES.teamFinancials(customerId, team.id)}
                          size="small"
                        >
                          <AttachMoneyIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </FinanceOnly>
                    <Tooltip title={intl.formatMessage({ id: "gestion.edit" })}>
                      <IconButton onClick={() => openEditDialog(team)} size="small">
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip
                      title={intl.formatMessage({
                        id: team.archived ? "gestion.unarchive" : "gestion.archive",
                      })}
                    >
                      <IconButton onClick={() => toggleArchive(team)} size="small">
                        {team.archived ? <UnarchiveIcon fontSize="small" /> : <ArchiveIcon fontSize="small" />}
                      </IconButton>
                    </Tooltip>
                  </>
                ),
              },
            ]}
            rows={teams}
          />
        </Box>
      )}

      <GestionListPagination
        page={page}
        totalPages={totalPages}
        pageSize={pageSize}
        totalResults={totalResults}
        onPageChange={handlePageChange}
        onPageSizeChange={handlePageSizeChange}
        disabled={initialLoading || refreshing}
      />
      </Stack>

      <Dialog
        open={dialogOpen}
        onClose={() => {
          setDialogOpen(false);
          setPendingImageFile(null);
        }}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>
          {intl.formatMessage({ id: editingTeam ? "gestion.teams.edit" : "gestion.teams.create" })}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TeamImageUpload
              team={editingTeam}
              onFileSelect={handleImageSelect}
              uploading={uploadingImage}
              saveFirst={!editingTeam?.id}
            />
            <TextField
              label={intl.formatMessage({ id: "gestion.teams.name" })}
              value={form.name}
              onChange={(event) => setForm((prev) => ({ ...prev, name: event.target.value }))}
              required
              fullWidth
            />
            <TextField
              label={intl.formatMessage({ id: "gestion.teams.shortName" })}
              value={form.short_name}
              onChange={(event) => setForm((prev) => ({ ...prev, short_name: event.target.value }))}
              fullWidth
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" color="primary" onClick={() => setDialogOpen(false)}>
            {intl.formatMessage({ id: "Cancelar" })}
          </Button>
          <Button variant="contained" color="primary" onClick={handleSave} disabled={saving || !form.name.trim()}>
            {intl.formatMessage({ id: saving ? "Guardando..." : "Guardar" })}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default GestionTeams;
